import { useAppModals, useAuth } from '@hb/react'
import { FieldMapValue, nameField } from '@hb/shared'
import { doc } from 'firebase/firestore'
import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from '../../hooks/navigation'
import { USERS_REF } from '../../backend/db/collections'
import { useUpdateDoc } from '../../hooks/backend/useUpdateDoc'
import { GenericEditModal } from '../DataView/GenericEditModal'

export const useNameEntryForm = () => {
  const { user, authUser } = useAuth()
  const { fname, lname, nickname, middleName, pronouns } = user ?? {}
  const value = useMemo(
    () => ({ fname, lname, nickname, middleName, pronouns }),
    [fname, lname, nickname, middleName, pronouns],
  )

  const ref = useMemo(() => (authUser ? doc(USERS_REF, authUser.uid) : null), [authUser])

  const update = useUpdateDoc('your info')
  const onUpdate = useCallback(
    (data: FieldMapValue) =>
      update(ref, '', {
        fname: (data.fname ?? '').trim(),
        lname: (data.lname ?? '').trim(),
        middleName: (data.middleName ?? '').trim(),
        nickname: (data.nickname ?? '').trim(),
        pronouns: (data.pronouns ?? '').trim(),
      }),
    [ref, update],
  )

  const onUpdateField = useCallback(
    (field: string, value: string) => update(ref, field, value),
    [ref, update],
  )

  return { value, onUpdate, onUpdateField }
}

export const NameEntryModal = ({ id = 'root' }: { id?: string }) => {
  const { openId, close, open } = useAppModals(s => s.nameEntry)
  const navigate = useNavigate()
  const { onUpdate, value } = useNameEntryForm()

  const user = useAuth(s => s.user)

  useEffect(() => {
    if (user && (!user.fname || !user.lname)) {
      open()
      return
    }
    close()
  }, [user, close, navigate, open])

  return openId === id ? (
    <GenericEditModal
      isOpen
      onClose={close}
      closeDisabled
      onSubmit={data => onUpdate(data)}
      data={value}
      field={nameField}
    />
  ) : null
}
